import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { fetchUsers } from "../../services/apiService";
import { useNavigate, useParams } from 'react-router';
import { useTelemetryContext } from "../../contexts/TelemetryContext";
import user from "../../img/user-avatar.png";
import TablePagination from '../reused-components/TablePagination';
import {
    usePersistedPagination,
    useResetPageOnFilterChange,
} from '../../hooks/usePersistedPagination';
import PlanBadge from '../reused-components/PlanBadge';
import { useProductSlugs } from '../../hooks/useProductSlugs';
import { useUserPlanTiers } from '../../hooks/useUserPlanTiers';

function UserTables({ paginationPerPage, searchTerm, onDataLoad }) {
    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [fetching, setFetching] = useState(false);
    const navigate = useNavigate();
    const { storeId, productId } = useParams();
    const { selectedProduct } = useTelemetryContext();
    const { freeSlug, proSlug } = useProductSlugs();
    const planByUserId = useUserPlanTiers(
        data,
        selectedProduct?.id,
        freeSlug,
        proSlug
    );
    const storageKey = `users-${productId || 'default'}`;
    const {
        currentPage,
        perPage,
        setCurrentPage,
        setPerPage,
        resetToFirstPage,
        paginationDefaultPage,
    } = usePersistedPagination(storageKey, paginationPerPage || 10);

    useResetPageOnFilterChange(resetToFirstPage, [searchTerm]);

    useEffect(() => {
        if (selectedProduct && selectedProduct.id.toString() === productId) {
            const fetchData = async () => {
                setFetching(true);
                try {
                    const responseData = await fetchUsers(selectedProduct.id, currentPage, perPage, searchTerm);
                    const rows = responseData.data || [];
                    const total = responseData.total || 0;
                    setData(rows);
                    setTotalRows(total);
                    if (onDataLoad) onDataLoad({ data: rows, total });
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setFetching(false);
                }
            };
            fetchData();
        }
    }, [selectedProduct, productId, currentPage, perPage, searchTerm]);

    const handleUserDetailClick = (userId) => {
        navigate(`/stores/${storeId}/plugins/${productId}/users/${userId}`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
        resetToFirstPage();
    };

    const columns = [
        {
            name: 'ID',
            selector: row => row.id,
            sortable: true,
            cell: row => (
                <span className="id-span link-icon" onClick={() => handleUserDetailClick(row.id)}>
                    {row.id}
                </span>
            ),
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
            cell: row => (
                <span className="data-user">
                    <img src={user} alt="avatar" />
                    <span className="link-icon customer-email-cell" onClick={() => handleUserDetailClick(row.id)}>
                        {row.email}
                        <PlanBadge tier={planByUserId[row.id]} />
                        {row.email_verification === 1 && (
                            <span className="verified-tooltip">
                                &nbsp;&nbsp;✔️
                                <span className="tooltip-text">Email address verified</span>
                            </span>
                        )}
                    </span>
                </span>
            ),
        },
        {
            name: 'Name',
            selector: row => row.nick_name,
            sortable: true,
            cell: row => <span className="id-span">{row.nick_name}</span>,
        },
        {
            name: 'Marketing',
            selector: row => row.marketing_record,
            sortable: true,
            cell: row => (
                <span className={"badge" + (row.marketing_record === 1 ? " green-badge" : "")}>
                    {row.marketing_record === 1 ? 'Allowed' : ''}
                </span>
            ),
        },
        {
            name: 'Member Since',
            selector: row => new Date(row.created_at).toLocaleDateString(),
            sortable: true,
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={perPage}
            paginationDefaultPage={paginationDefaultPage}
            paginationComponent={TablePagination}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            responsive
            progressPending={fetching}
            progressComponent={(
                <div className="spinner-container">
                    <div className="spinner"></div>
                </div>
            )}
            highlightOnHover
            className="custom-data-table"
        />
    );
}

export default UserTables;
